import { useState, useEffect, type ReactNode } from 'react'
import styles from './Sidebar.module.css'

export interface SidebarGroupProps {
  label: string
  defaultOpen?: boolean
  /** Optional localStorage key — when set, open/closed state persists across reloads */
  storageKey?: string
  /** When true, forces the group open regardless of user-toggled state (used during search). */
  forceOpen?: boolean
  /** Rendered inside a section, under its heading — styled as a sub-heading. */
  nested?: boolean
  children: ReactNode
}

const STORAGE_PREFIX = 'nexus-sidebar-group:'

export function SidebarGroup({ label, defaultOpen = true, storageKey, forceOpen, nested, children }: SidebarGroupProps) {
  const [userOpen, setUserOpen] = useState(() => {
    if (storageKey && typeof window !== 'undefined') {
      const stored = window.localStorage.getItem(STORAGE_PREFIX + storageKey)
      if (stored !== null) return stored === 'true'
    }
    return defaultOpen
  })

  useEffect(() => {
    if (storageKey && typeof window !== 'undefined') {
      window.localStorage.setItem(STORAGE_PREFIX + storageKey, String(userOpen))
    }
  }, [userOpen, storageKey])

  const isOpen = forceOpen || userOpen
  const setIsOpen = (updater: (prev: boolean) => boolean) => setUserOpen(updater)

  return (
    <div className={[styles.section, nested && styles.groupNested].filter(Boolean).join(' ')}>
      <button
        type="button"
        className={styles.groupTrigger}
        onClick={() => setIsOpen((v) => !v)}
        aria-expanded={isOpen}
      >
        <span className={styles.groupTriggerLabel}>{label}</span>
        <svg
          className={[styles.groupChevron, isOpen && styles.groupChevronOpen].filter(Boolean).join(' ')}
          width="10"
          height="10"
          viewBox="0 0 12 12"
          fill="none"
          aria-hidden="true"
        >
          <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <div className={[styles.groupContent, isOpen && styles.groupContentOpen].filter(Boolean).join(' ')}>
        <div className={styles.groupContentInner}>
          {/* Named group, so the section is still announced when the sidebar
              collapses and the trigger above is removed entirely. */}
          <div className={styles.sectionItems} role="group" aria-label={label}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )
}
